/**
 * <rlhf-thumbs> — the one-gesture quality signal: thumbs up / thumbs down, reversible.
 *
 * The cheapest possible feedback affordance, and deliberately the THINNEST: it is a
 * signal, not a rubric (that is `<rlhf-dimension-grader>`'s job). It costs one click, it
 * asks for no reason, and clicking the lit thumb again withdraws it.
 *
 * **Barely notable.** On a fine-pointer device it is INVISIBLE at rest and revealed when
 * the judged thing is hovered (a CSS-only reveal — see `styles.ts`). It is never removed
 * from the layout, the tab order or the a11y tree, so keyboard and screen-reader users
 * reach it exactly as if it were always painted; `:focus-within` reveals it for sighted
 * keyboard users. Where hover does not exist (`@media (hover: none)` — every touch
 * device) the reveal is skipped and the control is permanently present at low emphasis
 * with a 44px target: hiding is the PROGRESSIVE ENHANCEMENT, not the base state, so a
 * phone can never end up with an unreachable control. A recorded signal always shows,
 * hover or not — a judgement is state, not chrome.
 *
 * **The join seam.** A thumb is worth nothing unless one can say WHAT was judged and WHAT
 * produced it. `subjectRef` names the judged thing; `runRef` (+ optional `stepIndex`)
 * names the captured run/step behind it, so a consumer's console can join
 * signal → run → step → model → cost and rank models by cost/value. `stepIndex` is
 * omitted when the whole run's product is judged rather than one call. See
 * {@link SignalProvenance} in `@platform/rlhf-core`.
 *
 * Persistence is the host's, as everywhere in this package: the element appends
 * `signal.Recorded` / `signal.Cleared` into the injected {@link RlhfSessionStore}.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import type { RecordedSignal, SessionState, SignalPolarity, SignalProvenance } from "@platform/rlhf-core"
import { RlhfSessionStoreToken, type RlhfSessionStore } from "./store"
import { systemClock, type RlhfClock } from "./clock"

/** `stepIndex` is unset as `null` (an attribute-friendly absence), never as `-1`. */
export type StepIndex = number | null

export class ThumbsVm implements ViewModel {
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  private readonly storeState: ReadonlySignal<SessionState>

  constructor(
    private readonly store: RlhfSessionStore,
    readonly subjectRef: ReadonlySignal<string>,
    readonly runRef: ReadonlySignal<string>,
    readonly stepIndex: ReadonlySignal<StepIndex>,
    readonly label: ReadonlySignal<string>,
    private readonly actor: ReadonlySignal<string>,
    private readonly clock: RlhfClock,
  ) {
    this.storeState = store.state()
    this.renderDeps = [subjectRef, runRef, stepIndex, label, this.storeState]
  }

  /** The active signal for this subject, or `null` when none was given (or it was withdrawn). */
  current(): RecordedSignal | null {
    const ref = this.subjectRef.value
    if (!ref) return null
    return this.storeState.value.signals.find((s) => s.subjectRef === ref) ?? null
  }

  /**
   * The run/step this subject came from — omitted entirely when the host did not name a
   * run. An absent `runRef` means the signal cannot be joined to a model or a cost; it is
   * still recorded (a judgement of a human-authored subject is legitimate), and
   * {@link joinable} lets a host surface say so.
   */
  provenance(): SignalProvenance | undefined {
    const runRef = this.runRef.value
    if (!runRef) return undefined
    const step = this.stepIndex.value
    return step === null ? { runRef } : { runRef, stepIndex: step }
  }

  /** True when this signal can be joined to a captured run (and thus to model + cost). */
  joinable(): boolean {
    return this.runRef.value.length > 0
  }

  /**
   * Give (or withdraw) the signal. Clicking the lit thumb clears it; clicking the other
   * one flips it. Both are appends — the log keeps the reviewer's change of mind.
   */
  toggle(polarity: SignalPolarity): void {
    const subjectRef = this.subjectRef.value
    if (!subjectRef) return // nothing named ⇒ nothing to judge
    const at = this.clock.now()
    const actor = this.actor.value
    if (this.current()?.polarity === polarity) {
      this.store.append({ type: "signal.Cleared", at, actor, subjectRef })
      return
    }
    const producedBy = this.provenance()
    this.store.append({
      type: "signal.Recorded",
      at,
      actor,
      signal: { subjectRef, polarity, ...(producedBy ? { producedBy } : {}) },
    })
  }
}

export class RlhfThumbs extends KitElement<ThumbsVm> {
  readonly #subjectRef = signal("")
  readonly #runRef = signal("")
  readonly #stepIndex = signal<StepIndex>(null)
  readonly #actor = signal("operator")
  readonly #label = signal("Bewertung")
  #clock: RlhfClock = systemClock()

  /** WHAT is judged. Without it the element renders nothing — it would signal about nothing. */
  get subjectRef(): string {
    return this.#subjectRef.value
  }
  set subjectRef(v: string) {
    this.#subjectRef.set(v)
  }
  /** WHICH run produced the subject (opaque ref) — the join key to workflow, model and cost. */
  get runRef(): string {
    return this.#runRef.value
  }
  set runRef(v: string) {
    this.#runRef.set(v)
  }
  /** WHICH captured call within that run; `null` judges the run's whole product. */
  get stepIndex(): StepIndex {
    return this.#stepIndex.value
  }
  set stepIndex(v: StepIndex) {
    this.#stepIndex.set(v)
  }
  get actor(): string {
    return this.#actor.value
  }
  set actor(v: string) {
    this.#actor.set(v)
  }
  /** The group's accessible name — say WHAT is being judged ("Bewertung: Anschreiben"). */
  get label(): string {
    return this.#label.value
  }
  set label(v: string) {
    this.#label.set(v)
  }
  set clock(c: RlhfClock) {
    this.#clock = c
  }

  protected createViewModel(ctx: KitContext): ThumbsVm {
    return new ThumbsVm(
      ctx.inject(RlhfSessionStoreToken),
      this.#subjectRef,
      this.#runRef,
      this.#stepIndex,
      this.#label,
      this.#actor,
      this.#clock,
    )
  }

  /** One thumb. A real `<button>` with `aria-pressed` — a toggle, which is what it is. */
  #thumb(polarity: SignalPolarity, active: boolean): TemplateResult {
    const up = polarity === "up"
    return html`<button
      type="button"
      class=${classMap({ "rlhf-thumb": true, [`is-${polarity}`]: true, "is-active": active })}
      data-act="thumb"
      data-polarity=${polarity}
      aria-pressed=${active ? "true" : "false"}
      aria-label=${up ? "Hilfreich" : "Nicht hilfreich"}
      title=${up ? "Hilfreich" : "Nicht hilfreich"}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" class=${up ? "" : "is-flipped"}>
        <path
          d="M7 21V10.2l4.6-7.2c.9 0 1.7.7 1.7 1.7v3.9h4.9c1.2 0 2.1 1.1 1.8 2.3l-1.6 7.1c-.2 1.1-1.2 1.8-2.3 1.8H7Z"
        />
        <path d="M7 10.5H4.4c-.8 0-1.4.6-1.4 1.4v7.7c0 .8.6 1.4 1.4 1.4H7" />
      </svg>
    </button>`
  }

  protected render(vm: ThumbsVm): TemplateResult {
    const subjectRef = vm.subjectRef.value
    if (!subjectRef) return html`${nothing}`
    const current = vm.current()
    const polarity = current?.polarity ?? null
    const step = vm.stepIndex.value
    return html`<span
      class=${classMap({ "rlhf-thumbs": true, "is-set": polarity !== null })}
      role="group"
      aria-label=${vm.label.value}
      data-subject=${subjectRef}
      data-run=${vm.runRef.value || nothing}
      data-step=${step === null ? nothing : String(step)}
      data-joinable=${vm.joinable() ? "true" : "false"}
    >
      ${this.#thumb("up", polarity === "up")}${this.#thumb("down", polarity === "down")}
      <span class="rlhf-vh" role="status" lang="de"
        >${polarity === null ? "" : polarity === "up" ? "Als hilfreich bewertet" : "Als nicht hilfreich bewertet"}</span
      >
    </span>`
  }

  protected override actions(vm: ThumbsVm): ActionMap {
    return {
      thumb: ({ polarity }) => {
        if (polarity === "up" || polarity === "down") vm.toggle(polarity)
      },
    }
  }
}
